import axios, { AxiosRequestConfig } from 'axios';
import { Environment as environment } from '../environment.ts';

// ── Queue for requests that arrive while a refresh is already in-flight ──────
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(p => (error ? p.reject(error) : p.resolve(token!)));
  failedQueue = [];
};

// ── Response interceptor: catch 401, refresh once, retry queued requests ──────
axios.interceptors.response.use(
  response => response,
  async (error) => {
    const original: AxiosRequestConfig & { _retry?: boolean } = error.config ?? {};

    const is401 =
      error.response?.status === 401 &&
      !original._retry &&
      !original.url?.includes('auth/refresh');

    if (!is401) {
      return Promise.reject(error);
    }

    // If a refresh is already running, queue this request
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        if (original.headers) {
          original.headers['Authorization'] = `Bearer ${token}`;
        }
        return axios(original);
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const res = await axios.get(
        `${environment.baseUrl}/auth/refresh`,
        { withCredentials: true }   // backend reads refresh token from HttpOnly cookie
      );

      const newToken: string =
        res.data?.data?.accessToken ??
        res.data?.accessToken ??
        '';

      // Update the default header so every subsequent request uses the new token
      setAuthToken(newToken);

      if (original.headers) {
        original.headers['Authorization'] = `Bearer ${newToken}`;
      }

      processQueue(null, newToken);
      return axios(original);
    } catch (refreshError) {
      processQueue(refreshError, null);
      // Clear the stale token so callers know the session is dead
      delete axios.defaults.headers.common['Authorization'];
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

/**
 * Call this once at app start (Canvas.tsx) when the token arrives via URL param.
 * All axios calls — regardless of which file they come from — will use it.
 */
export function setAuthToken(token: string): void {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}
